
import React, { useState } from 'react';
import { InterviewRequestsFacade } from '@/services/interview';
import { toast } from 'sonner';
import { Trash } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface DeleteAllDialogProps {
  onDeleteSuccess: () => void;
}

const DeleteAllDialog: React.FC<DeleteAllDialogProps> = ({ onDeleteSuccess }) => {
  const { t } = useTranslation();
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [isDeleteAllDialogOpen, setIsDeleteAllDialogOpen] = useState(false);
  
  // Handle deleting all interview requests
  const handleDeleteAllRequests = async () => {
    try {
      setIsDeletingAll(true);
      console.log('Deleting all interview requests...');
      
      const result = await InterviewRequestsFacade.deleteAllInterviewRequests();
      
      if (result.success) {
        toast.success(t('success.deleted'));
        onDeleteSuccess();
        console.log('All interview requests deleted successfully');
      } else {
        console.error('Failed to delete all requests:', result.error);
        toast.error(`${t('errors.deleteError')}: ${result.error || t('interview.unknownError')}`);
      }
    } catch (err) {
      console.error('Error deleting all requests:', err);
      toast.error(t('errors.deleteError'));
    } finally {
      setIsDeletingAll(false);
      setIsDeleteAllDialogOpen(false);
    }
  };

  return (
    <AlertDialog open={isDeleteAllDialogOpen} onOpenChange={setIsDeleteAllDialogOpen}>
      <AlertDialogTrigger asChild>
        <Button 
          variant="destructive" 
          size="sm"
          className="flex items-center gap-1"
        >
          <Trash className="w-4 h-4" />
          {t('interview.deleteAll')}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('interview.deleteAllConfirm')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('interview.deleteAllDescription')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel 
            onClick={() => setIsDeleteAllDialogOpen(false)}
            className="bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300"
          >
            {t('common.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleDeleteAllRequests}
            className="bg-red-600 hover:bg-red-700 text-white"
            disabled={isDeletingAll}
          >
            {isDeletingAll ? t('loading.deleting') : t('interview.deleteAll')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteAllDialog;

